import GridStack from 'gridstack/dist/gridstack-all.js';
export class GridStackManager {
  constructor(gridRef, options = {}) {
    this.gridRef = gridRef;
    this.grid = null;
    this.resizeObserver = null;
    this.lastWidgetsPerRow = null;
    this.widgetConfig = options.widgetConfig || null;
    this.defaultWidgetConfig = options.widgetConfig || null; // Store default config as fallback
    this.widgetIdPrefix = options.widgetIdPrefix || 'card-';
    this.resizeTimer = null;
    this.isCustomizeMode = false; // Track customize mode ourselves
    this.isUpdatingHeight = false; // Flag to prevent layout recalculation during height updates
    this.options = {
      cellHeight: options.cellHeight || 540,
      autoCellHeight: false,
      column: 12,
      draggable: { handle: '.card-drag' },
      ...options
    };
    this.defaultWidgetOrder = [];
  }

  // Trigger chart resize with debounce to avoid multiple rapid calls
  triggerChartResize() {
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    this.resizeTimer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      this.resizeTimer = null;
    }, 150);
  }

  setDefaultWidgetOrder(widgets) {
    this.defaultWidgetOrder = [...widgets];
  }

  setWidgetConfig(widgetConfig) {
    this.widgetConfig = widgetConfig;
  }

  // Restore widget configuration from saved layout data
  restoreFromSavedOrder(savedData, defaultConfig) {
    // Always store the default config as fallback for widgets not in saved layout
    this.defaultWidgetConfig = defaultConfig;

    if (!savedData) {
      this.setWidgetConfig(defaultConfig);
      return;
    }

    // Handle both old format (array of widget IDs) and new format (layout data)
    if (Array.isArray(savedData) && typeof savedData[0] === 'string') {
      // Old format - just widget order
      // Start with all default widgets, then override with saved positions
      const restoredConfig = { ...defaultConfig };
      let currentRow = 1;
      let widgetsInCurrentRow = 0;
      const maxWidgetsPerRow = 3;

      savedData.forEach((widgetId) => {
        const originalWidget = defaultConfig[widgetId];
        if (originalWidget) {
          if (widgetsInCurrentRow >= maxWidgetsPerRow) {
            currentRow++;
            widgetsInCurrentRow = 0;
          }

          restoredConfig[widgetId] = {
            ...originalWidget,
            row: currentRow
          };

          widgetsInCurrentRow++;
        }
      });

      this.setWidgetConfig(restoredConfig);
    } else if (savedData.layoutData && savedData.layoutData.length > 0) {
      // New format - complete layout data
      // Start with all default widgets, then override with saved positions
      const restoredConfig = { ...defaultConfig };

      savedData.layoutData.forEach(item => {
        const originalWidget = defaultConfig[item.id];
        if (originalWidget) {
          restoredConfig[item.id] = {
            title: originalWidget.title,
            w: item.w,
            h: item.h,
            isMetric: originalWidget.isMetric || false,
            linkTo: originalWidget.linkTo,
            row: item.row,
            x: item.x,
            y: item.y
          };
        }
      });

      this.setWidgetConfig(restoredConfig);
    } else {
      // Fallback to default
      this.setWidgetConfig(defaultConfig);
    }
  }

  calculateResponsiveLayout(selectedWidgets, widgetConfig = null) {
    const containerWidth = this.gridRef.current ? this.gridRef.current.offsetWidth : 1200;
    
    const layout = [];
    const config = widgetConfig || this.widgetConfig;
    
    // For small screens (< 840px), use simple sequential layout
    if (containerWidth < 840) {
      selectedWidgets.forEach((widgetId, index) => {
        layout.push({
          id: selectedWidgets[index],
          x: 0,
          y: index,
          w: 12,
          h: 1
        });
      });
      return layout;
    }
    
    // For larger screens, use the widget configuration
    if (!config) {
      // Fallback dynamic calculation for larger screens
      const widgetsPerRow = containerWidth < 1200 ? 2 : 3;
      const numRows = Math.ceil(selectedWidgets.length / widgetsPerRow);
      let currentIndex = 0;
      
      for (let row = 0; row < numRows; row++) {
        const widgetsInRow = Math.min(widgetsPerRow, selectedWidgets.length - currentIndex);
        const adjustedColumnWidth = 12 / widgetsInRow;
        
        for (let col = 0; col < widgetsInRow; col++) {
          if (currentIndex < selectedWidgets.length) {
            layout.push({
              id: selectedWidgets[currentIndex],
              x: col * adjustedColumnWidth,
              y: row,
              w: adjustedColumnWidth,
              h: 1
            });
            currentIndex++;
          }
        }
      }
      return layout;
    }
    
    // Group widgets by their fixed row positions
    const widgetsByRow = {};

    selectedWidgets.forEach(widgetId => {
      // Ensure we use the clean ID to look up the configuration
      const cleanWidgetId = widgetId.replace(this.widgetIdPrefix, '');
      // Try saved config first, then fall back to default config
      const widget = config[cleanWidgetId] || (this.defaultWidgetConfig && this.defaultWidgetConfig[cleanWidgetId]);
      const row = widget ? widget.row : 1; // Default to row 1 if not found

      if (!widgetsByRow[row]) {
        widgetsByRow[row] = [];
      }
      widgetsByRow[row].push(widgetId);
    });
    
    // Sort rows and create layout
    const sortedRows = Object.keys(widgetsByRow).sort((a, b) => parseInt(a) - parseInt(b));
    
    sortedRows.forEach((row, rowIndex) => {
      const rowWidgets = widgetsByRow[row];
      const adjustedColumnWidth = 12 / rowWidgets.length;
      
      rowWidgets.forEach((widgetId, indexInRow) => {
        layout.push({
          id: widgetId,
          x: indexInRow * adjustedColumnWidth,
          y: rowIndex,
          w: adjustedColumnWidth,
          h: 1
        });
      });
    });
    
    return layout;
  }

  async loadGridStack() {
    if (this.grid || !this.gridRef.current) return;

    this.grid = GridStack.init({
      ...this.options,
      staticGrid: true,
    }, this.gridRef.current);

    this.setupDragAndDrop();
    this.setupResizeObserver();

    return this.grid;
  }

  setupResizeObserver() {
    if (!this.grid || !this.gridRef.current) return;

    this.updateColumnCount();

    this.resizeObserver = new ResizeObserver(() => {
      this.updateColumnCount();
    });

    this.resizeObserver.observe(this.gridRef.current);
  }

  updateColumnCount() {
    if (!this.grid || !this.gridRef.current || this.isDragging || this.isUpdatingHeight) return;

    const currentWidgets = this.grid.engine.nodes.map(node => node.el.id.replace(this.widgetIdPrefix, ''));
    this.recalculateLayout(currentWidgets);
  }

  recalculateLayout(selectedWidgets) {
    if (!this.grid) return;

    if (selectedWidgets.length > 0) {
      this.grid.removeAll(false);
      const layout = this.calculateResponsiveLayout(selectedWidgets);

      const allWidgets = Array.from(this.gridRef.current.querySelectorAll('.grid-stack-item'));
      const widgetMap = allWidgets.reduce((map, widget) => {
        map[widget.id] = widget;
        return map;
      }, {});

      layout.forEach(item => {
        const widgetId = `${this.widgetIdPrefix}${item.id}`;
        const widget = widgetMap[widgetId] || widgetMap[item.id];
        if (widget) {
          // Remove any existing grid attributes
          widget.removeAttribute('gs-x');
          widget.removeAttribute('gs-y');
          widget.removeAttribute('gs-w');
          widget.removeAttribute('gs-h');

          this.grid.makeWidget(widget, {
            x: item.x,
            y: item.y,
            w: item.w,
            h: item.h,
            draggable: true
          });
        }
      });

      // Force a layout update
      this.grid.batchUpdate(false);
    }
  }

  updateWidgets(selectedWidgets) {
    this.recalculateLayout(selectedWidgets);
  }

  // Update widget height without triggering full layout recalculation
  updateWidgetHeight(element, newHeight) {
    if (!this.grid || !element) return;

    this.isUpdatingHeight = true;
    try {
      this.grid.update(element, { h: newHeight });
    } finally {
      // Use setTimeout to ensure the flag is reset after any async operations
      setTimeout(() => {
        this.isUpdatingHeight = false;
      }, 100);
    }
  }

  handleCustomizeViewClick = (selectedWidgets) => {
    if (!this.grid) return;
    this.isCustomizeMode = true;
    this.grid.setStatic(false);
    this.recalculateLayout(selectedWidgets);
    // Re-setup drag and drop handlers after layout recalculation
    this.setupDragAndDrop();
  };

  handleGoBackFromCustomize = (selectedWidgets, callback) => {
    if (!this.grid) {
      if (callback) callback();
      return;
    }
    this.isCustomizeMode = false;
    this.grid.setStatic(true);
    this.recalculateLayout(selectedWidgets);
    //this.triggerChartResize();
    if (callback) {
      callback();
    }
  };

  handleApplyCustomization = () => {
    if (!this.grid) return { widgets: [], layoutData: [] };

    // Get current widget positions and create complete layout data
    const currentNodes = this.grid.engine.nodes
      .sort((a, b) => {
        if (a.y === b.y) {
          return a.x - b.x;
        }
        return a.y - b.y;
      });

    // If no nodes, return empty data
    if (!currentNodes || currentNodes.length === 0) {
      return { widgets: [], layoutData: [] };
    }

    // Create layout data with position and size information
    const layoutData = currentNodes.map((node, index) => {
      const widgetId = node.el.id.replace(this.widgetIdPrefix, '');
      return {
        id: widgetId,
        order: index,
        x: node.x,
        y: node.y,
        w: node.w,
        h: node.h,
        row: node.y + 1 // Convert 0-based y to 1-based row
      };
    });

    // Update the widget configuration with new layout
    const newConfig = {};
    layoutData.forEach(item => {
      // Try current config first, then fall back to default config
      const originalWidget = (this.widgetConfig && this.widgetConfig[item.id]) || (this.defaultWidgetConfig && this.defaultWidgetConfig[item.id]);
      newConfig[item.id] = {
        title: originalWidget?.title || item.id,
        w: item.w,
        h: item.h,
        isMetric: originalWidget?.isMetric || false,
        linkTo: originalWidget?.linkTo,
        row: item.row,
        x: item.x,
        y: item.y
      };
    });

    // Update the widget configuration
    this.setWidgetConfig(newConfig);
    
    // Return complete layout data for saving
    const orderedWidgets = layoutData.map(item => item.id);
    
    this.handleGoBackFromCustomize(orderedWidgets);
    return { widgets: orderedWidgets, layoutData };
  };

  handleTempWidgetSelectionChange = (selectedWidgets) => {
    if (!this.grid) return;

    // Must recalculate layout so remaining widgets fill the row
    // Set non-static BEFORE recalculating so widgets are created as draggable
    if (this.isCustomizeMode) {
      this.grid.setStatic(false);
    }

    this.recalculateLayout(selectedWidgets);

    // Re-setup drag and drop handlers after layout recalculation
    if (this.isCustomizeMode) {
      this.grid.setStatic(false);
      this.setupDragAndDrop();
    } else {
      // Only trigger chart resize when NOT in customize mode
      // In customize mode, this causes a loop that recalculates layout and undoes swaps
      this.triggerChartResize();
    }
  };

  setupDragAndDrop() {
    if (!this.grid) return;

    // Clear all existing drag events
    this.grid.off('dragstop');
    this.grid.off('dragstart');
    this.grid.off('dropped');

    // Track drag state
    this.isDragging = false;
    let draggedElement = null;
    let originalPositions = {};

    this.grid.on('dragstart', (event, el) => {
      this.isDragging = true;
      draggedElement = el;

      // Store original positions of all widgets before drag
      originalPositions = {};
      this.grid.engine.nodes.forEach(node => {
        originalPositions[node.el.id] = {
          x: node.x,
          y: node.y,
          w: node.w,
          h: node.h
        };
      });
    });

    this.grid.on('dragstop', (event, el) => {
      if (!draggedElement || !originalPositions[draggedElement.id]) {
        this.isDragging = false;
        draggedElement = null;
        originalPositions = {};
        return;
      }

      const draggedOriginal = originalPositions[draggedElement.id];
      const draggedNode = this.grid.engine.nodes.find(node => node.el === draggedElement);

      if (!draggedNode) {
        this.isDragging = false;
        draggedElement = null;
        originalPositions = {};
        return;
      }

      // Find the widget we're swapping with based on current position
      let targetNode = null;
      let targetOriginal = null;
      let bestScore = -1;

      // Calculate center of dragged widget's new position
      const draggedCenterX = draggedNode.x + draggedNode.w / 2;
      const draggedCenterY = draggedNode.y + draggedNode.h / 2;

      for (const node of this.grid.engine.nodes) {
        if (node.el === draggedElement) continue;

        const original = originalPositions[node.el.id];
        if (!original) continue;

        // Calculate center of this widget's original position
        const targetCenterX = original.x + original.w / 2;
        const targetCenterY = original.y + original.h / 2;

        // Check if dragged widget overlaps with this widget's original position
        const overlapX = Math.max(0,
          Math.min(draggedNode.x + draggedNode.w, original.x + original.w) -
          Math.max(draggedNode.x, original.x)
        );
        const overlapY = Math.max(0,
          Math.min(draggedNode.y + draggedNode.h, original.y + original.h) -
          Math.max(draggedNode.y, original.y)
        );

        // Calculate distance between centers
        const distance = Math.sqrt(
          Math.pow(draggedCenterX - targetCenterX, 2) +
          Math.pow(draggedCenterY - targetCenterY, 2)
        );

        // Score based on overlap (primary) and proximity (secondary)
        // Higher overlap = better, closer distance = better
        const overlapArea = overlapX * overlapY;
        const proximityScore = overlapArea > 0 ? 1000 + overlapArea : (distance < 2 ? 100 - distance * 10 : 0);

        if (proximityScore > bestScore) {
          bestScore = proximityScore;
          targetNode = node;
          targetOriginal = original;
        }
      }

      // Check if widget actually moved
      const hasMoved = draggedNode.x !== draggedOriginal.x || draggedNode.y !== draggedOriginal.y;

      if (targetNode && targetOriginal && hasMoved && bestScore > 0) {
        // Swap: dragged widget takes target's original position and width
        // Target widget takes dragged widget's original position and width
        this.grid.batchUpdate(true);

        this.grid.update(draggedElement, {
          x: targetOriginal.x,
          y: targetOriginal.y,
          w: targetOriginal.w,
          h: targetOriginal.h
        });

        this.grid.update(targetNode.el, {
          x: draggedOriginal.x,
          y: draggedOriginal.y,
          w: draggedOriginal.w,
          h: draggedOriginal.h
        });

        this.grid.batchUpdate(false);

        // Update widget config with new row assignments
        const draggedId = draggedElement.id.replace(this.widgetIdPrefix, '');
        const targetId = targetNode.el.id.replace(this.widgetIdPrefix, '');

        if (this.widgetConfig) {
          // Swap row assignments in config
          const draggedRow = this.widgetConfig[draggedId]?.row;
          const targetRow = this.widgetConfig[targetId]?.row;

          if (this.widgetConfig[draggedId]) {
            this.widgetConfig[draggedId].row = targetRow || (targetOriginal.y + 1);
            this.widgetConfig[draggedId].x = targetOriginal.x;
          }
          if (this.widgetConfig[targetId]) {
            this.widgetConfig[targetId].row = draggedRow || (draggedOriginal.y + 1);
            this.widgetConfig[targetId].x = draggedOriginal.x;
          }
        }
      } else {
        // No valid swap target or didn't move - restore original position
        this.grid.update(draggedElement, {
          x: draggedOriginal.x,
          y: draggedOriginal.y,
          w: draggedOriginal.w,
          h: draggedOriginal.h
        });
      }

      this.isDragging = false;
      draggedElement = null;
      originalPositions = {};
    });
  }

  destroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.grid) {
      this.grid.destroy(true); // true to remove DOM elements and widgets
      this.grid = null;
    }
  }

  // Clean up JS resources without manipulating DOM (let React handle DOM cleanup)
  cleanupWithoutDom() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.grid) {
      this.grid.destroy(false); // false = don't remove DOM elements
      this.grid = null;
    }
  }
}
